import React, { useState, useRef } from "react"
import { getPaletteColor } from "./chartTheme"

const DEFAULT_COLORS = [0, 1, 2, 0].map((i) => getPaletteColor(i).color1)

const CHART_H = 240
const CONTAINER_H = 360
const PAD_TOP = 40
const PAD_BOTTOM = 60
const BAR_W = 12
const BAR_GAP = 4

const formatYAxis = (val) => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(0)}M`
  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`
  return Math.round(val)
}

const barStyle = (color1, color2) =>
  color2
    ? { backgroundImage: `linear-gradient(to bottom, ${color1}, ${color2})` }
    : { backgroundColor: color1 }

// series: Array<{
//   name: string,
//   color1?: string,
//   color2?: string,
//   perBarColors?: Array<{ color1: string, color2?: string }>,  // per-bar color override; takes precedence over color1/color2
//   data: number[],
//   valueFormatter?: (val: number) => string,
//   tooltipContent?: (index: number) => ReactNode,  // fully custom tooltip row; replaces the default name/value line
// }>
// labels: string[]  — x-axis labels, one per data point
function GroupedBarChart({
  labels = [],
  series = [],
  barWidth = BAR_W,
  roundedBottom = false,
}) {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    index: null,
  })
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const containerRef = useRef(null)

  const allValues = series.flatMap((s) => s.data || [])
  const maxValue = Math.max(...allValues, 1)

  const groupWidth =
    series.length * barWidth + Math.max(series.length - 1, 0) * BAR_GAP
  // Minimum horizontal space each group occupies. Once the groups no longer
  // fit, the plot area scrolls horizontally instead of cramming them together.
  const minGroupWidth = Math.max(groupWidth + 36, 56)

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        height: CONTAINER_H,
        boxSizing: "border-box",
        zIndex: 0,
      }}
    >
      {/* Grid lines */}
      <div
        style={{
          position: "absolute",
          top: PAD_TOP,
          left: 0,
          right: 0,
          bottom: PAD_BOTTOM,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              position: "relative",
              width: "100%",
              borderTop: "2px dashed #e2e8f0",
              height: 0,
            }}
          >
            {i < 4 && (
              <span
                style={{
                  position: "absolute",
                  left: 8,
                  bottom: 4,
                  fontSize: 12,
                  color: "#64748b",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {formatYAxis((maxValue * (4 - i)) / 4)}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Baseline */}
      <div
        style={{
          position: "absolute",
          bottom: PAD_BOTTOM,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: "#e2e8f0",
          zIndex: 2,
        }}
      />

      {/* Tooltip */}
      {tooltip.visible && tooltip.index !== null && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            transform: "translate(-50%, -100%)",
            backgroundColor: "#1e293b",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 10,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            marginTop: -8,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6, color: "#e2e8f0" }}>
            {labels[tooltip.index]}
          </div>
          {series.map((s, si) => {
            const perBar = s.perBarColors?.[tooltip.index]
            const c1 = perBar?.color1 ?? s.color1 ?? DEFAULT_COLORS[si] ?? "#3D5AFE"
            const c2 = perBar?.color2 ?? s.color2 ?? null
            const raw = s.data[tooltip.index] ?? 0
            const formatted = s.valueFormatter ? s.valueFormatter(raw) : raw
            const customContent = s.tooltipContent?.(tooltip.index)
            const isLast = si === series.length - 1
            {console.log(customContent)}
            if (customContent) {
              return (
                <div key={si} style={{ marginBottom: isLast ? 0 : 6 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#e2e8f0",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        flexShrink: 0,
                        ...barStyle(c1, c2),
                      }}
                    />
                    <strong style={{ color: "#fff" }}>{s.name}</strong>
                  </div>
                  {customContent}
                </div>
              )
            }

            return (
              <div
                key={si}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#fff",
                  marginBottom: isLast ? 0 : 3,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    ...barStyle(c1, c2),
                  }}
                />
                {s.name}: <strong style={{ color: "#fff" }}>{formatted}</strong>
              </div>
            )
          })}
          <div
            style={{
              position: "absolute",
              bottom: -5,
              left: "50%",
              transform: "translateX(-50%)",
              width: 10,
              height: 10,
              backgroundColor: "#1e293b",
              clipPath: "polygon(0 0, 100% 0, 50% 100%)",
            }}
          />
        </div>
      )}

      {/* Scrollable plot area */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflowX: "auto",
          overflowY: "hidden",
          paddingLeft: 70,
          paddingRight: 20,
          boxSizing: "border-box",
          zIndex: 3,
        }}
      >
        <div
          style={{
            position: "relative",
            minWidth: "100%",
            height: "100%",
            display: "flex",
            alignItems: "flex-end",
            boxSizing: "border-box",
          }}
        >
          {/* Bars */}
          {labels.map((label, i) => (
            <div
              key={label}
              onMouseMove={(e) => {
                const rect = containerRef.current.getBoundingClientRect()
                setTooltip({
                  visible: true,
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top - 8,
                  index: i,
                })
                setHoveredIndex(i)
              }}
              onMouseLeave={() => {
                setTooltip((prev) => ({ ...prev, visible: false }))
                setHoveredIndex(null)
              }}
              style={{
                position: "relative",
                flex: 1,
                minWidth: minGroupWidth,
                height: "100%",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingBottom: PAD_BOTTOM,
                boxSizing: "border-box",
                zIndex: 3,
                cursor: "default",
              }}
            >
          {/* Hover highlight */}
          {hoveredIndex === i && (() => {
            const groupW = series.length * barWidth + (series.length - 1) * BAR_GAP
            const tallest = Math.max(...series.map((s) => (s.data[i] || 0) / maxValue * CHART_H))
            return (
              <div
                style={{
                  position: "absolute",
                  bottom: PAD_BOTTOM,
                  paddingBottom: 0,
                  width: groupW + 14,
                  height: tallest + 8,
                  borderTopLeftRadius: 18,
                  borderTopRightRadius: 18,
                  backgroundColor: "rgba(148, 163, 184, 0.18)",
                  pointerEvents: "none",
                }}
              />
            )
          })()}

          {/* Bar group */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: BAR_GAP,
            }}
          >
            {series.map((s, si) => {
              const val = s.data[i] || 0
              const height = Math.max(
                (val / maxValue) * CHART_H,
                val > 0 ? 4 : 0,
              )
              const perBar = s.perBarColors?.[i]
              const c1 = perBar?.color1 ?? s.color1 ?? DEFAULT_COLORS[si] ?? "#3D5AFE"
              const c2 = perBar?.color2 ?? s.color2 ?? null
              return (
                <div
                  key={si}
                  style={{
                    width: barWidth,
                    height,
                    minHeight: "5px",
                    borderRadius: roundedBottom ? "11px" : "11px 11px 0px 0px",
                    transition: "height 0.5s ease",
                    flexShrink: 0,
                    ...barStyle(c1, c2),
                  }}
                />
              )
            })}
          </div>

          {/* X-axis label */}
          <div
            style={{
              position: "absolute",
              bottom: 16,
              fontSize: 13,
              color: "#64748b",
              textAlign: "center",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {label}
          </div>
        </div>
      ))}
        </div>
      </div>
    </div>
  )
}

export default GroupedBarChart
